/**
 * Watchdog · Job-Manager (FR-7 + Patch 01)
 *
 * Cockpit fuer die Testphase: die Jobtabelle sehen, Jobs anlegen, einzeln oder
 * komplett ausfuehren, aufraeumen. Ersetzt Postman.
 *
 * Die Tabelle ist nach DOMAIN gruppiert: eine Zeile je Domain. Aufgeklappt
 * zeigt sie EINEN Lauf im Detail, dazu ein Dropdown, um zwischen den Laeufen
 * dieser Domain zu wechseln. Eine Domain wird oft mehrfach gescannt und
 * bewertet — flach gelistet ersaeuft die Uebersicht in Wiederholungen, und
 * eine Untertabelle mit zwanzig Zeilen liest auch niemand.
 *
 * Zwei Job-Arten in einer Tabelle (Patch 01, 20.08.2026):
 *   · raw_scan → der Scan-Lauf; `run_ref` ist sein ERGEBNIS (die Raw-Zeile)
 *   · scoring  → die Bewertung; `run_ref` ist ihr GEGENSTAND (welcher Lauf)
 *
 * Zum Anstossen (Befund 21.08.): Scans laufen ueber den START-Weg
 * (`/jobs/:id/start`) — anstossen, sofort zurueck, das Ergebnis kommt spaeter
 * per Callback. Der synchrone Weg `/jobs/:id/run` wartet auf eine Antwort, die
 * dieser n8n-Workflow nie synchron gibt.
 *
 * Zum db_profile (PRD-Entscheidung E1): hier wird nur ANGEZEIGT, welche
 * Umgebung gerade bedient wird. Gewechselt wird weiter ueber die Menueleiste.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, Chip, Button,
  CircularProgress, Alert, Tooltip, IconButton, Snackbar, Collapse, Stack, MenuItem, Select,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, LinearProgress,
  Autocomplete, TextField, FormControlLabel, Switch, Divider, InputAdornment,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StorageIcon from '@mui/icons-material/Storage';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import RuleIcon from '@mui/icons-material/Rule';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import type {
  JobListItem, JobListResponse, JobStatus, JobRunType, JobKind, ScanRunOption, DomainListItem,
} from '../../../shared/watchdogTypes.js';

/** Farbwelt der Job-Laeufe. Bewusst getrennt von der severity-Skala im Notification-Center. */
const STATUS_META: Record<JobStatus, { label: string; color: string; text: string }> = {
  pending: { label: 'wartet', color: colors.silver, text: colors.black85 },
  running: { label: 'läuft', color: '#3b82f6', text: colors.white },
  completed: { label: 'fertig', color: colors.scoreGreen, text: colors.white },
  failed: { label: 'fehlgeschlagen', color: colors.scoreRed, text: colors.white },
};

const RUN_TYPE_LABEL: Record<JobRunType, string> = {
  night_scan: 'Nacht-Lauf',
  manual: 'manuell',
};

/** Die zwei Job-Arten. `runRefLabel` benennt die Doppelrolle von run_ref. */
const KIND_META: Record<JobKind, { label: string; color: string; runRefLabel: string }> = {
  raw_scan: { label: 'Scan', color: '#0f766e', runRefLabel: 'Raw-Zeile (Ergebnis)' },
  scoring: { label: 'Bewertung', color: '#6b46c1', runRefLabel: 'bewerteter Lauf' },
};

const KINDS: JobKind[] = ['raw_scan', 'scoring'];
const POLL_MS = 10_000;
const STATUS_KEYS = Object.keys(STATUS_META) as JobStatus[];

const emptyCounts = (): Record<JobStatus, number> => ({ pending: 0, running: 0, completed: 0, failed: 0 });

function formatTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

/** Laufzeit eines Jobs, sobald beide Zeiten stehen. */
function duration(job: JobListItem): string | null {
  if (!job.started_at || !job.finished_at) return null;
  const ms = new Date(job.finished_at).getTime() - new Date(job.started_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return ms < 60_000 ? `${Math.round(ms / 1000)} s` : `${Math.round(ms / 60_000)} min`;
}

/** Alle Laeufe einer Domain, plus was man in der Kopfzeile sehen will. */
interface DomainGroup {
  key: string;
  hostname: string;
  workspace_ref: string | null;
  jobs: JobListItem[];
  counts: Record<JobStatus, number>;
  kinds: Record<JobKind, number>;
  /** Juengster Lauf — bestimmt die Sortierung der Gruppen. */
  latest: JobListItem;
}

function groupByDomain(jobs: JobListItem[]): DomainGroup[] {
  const map = new Map<string, DomainGroup>();

  for (const job of jobs) {
    const key = job.domain_fk;
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        hostname: job.hostname ?? '(Domain gelöscht)',
        workspace_ref: job.workspace_ref,
        jobs: [],
        counts: emptyCounts(),
        kinds: { raw_scan: 0, scoring: 0 },
        latest: job,
      };
      map.set(key, group);
    }
    group.jobs.push(job);
    group.counts[job.job_status] += 1;
    if (group.kinds[job.job_kind] !== undefined) group.kinds[job.job_kind] += 1;
    if (new Date(job.created_at).getTime() > new Date(group.latest.created_at).getTime()) {
      group.latest = job;
    }
  }

  const groups = [...map.values()];
  for (const g of groups) {
    // Innerhalb der Domain der neueste Lauf zuerst.
    g.jobs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }
  // Domains mit frischer Aktivitaet nach oben.
  groups.sort((a, b) => new Date(b.latest.created_at).getTime() - new Date(a.latest.created_at).getTime());
  return groups;
}

/** Beschriftung eines Laufs im Auswahl-Dropdown. */
function runLabel(job: JobListItem): string {
  const when = formatTime(job.started_at ?? job.created_at);
  const kind = KIND_META[job.job_kind]?.label ?? job.job_kind;
  const status = STATUS_META[job.job_status]?.label ?? job.job_status;
  return `${when} · ${kind} · ${status}`;
}

/** Ein Feld der Detailkarte. */
function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 130 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.25 }}>{children}</Box>
    </Box>
  );
}

export default function JobManagerPage() {
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [counts, setCounts] = useState<Record<JobStatus, number>>(emptyCounts());
  const [countsByKind, setCountsByKind] = useState<Record<JobKind, Record<JobStatus, number>>>({
    raw_scan: emptyCounts(), scoring: emptyCounts(),
  });
  const [dbProfile, setDbProfile] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | 'all'>('all');
  const [kindFilter, setKindFilter] = useState<JobKind | 'all'>('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Welcher Lauf wird je Domain gezeigt? Ohne Eintrag gilt der neueste.
  const [chosenRun, setChosenRun] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [expandedError, setExpandedError] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<JobListItem | null>(null);

  // ── Trigger 1: Raw-Scan starten ────────────────────────────
  const [rawDialog, setRawDialog] = useState(false);
  const [domains, setDomains] = useState<DomainListItem[]>([]);
  const [rawDomain, setRawDomain] = useState<DomainListItem | null>(null);

  // ── Trigger 2: Check and Score starten ─────────────────────
  const [scoreDialog, setScoreDialog] = useState(false);
  const [runs, setRuns] = useState<ScanRunOption[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [onlyUnscored, setOnlyUnscored] = useState(true);
  const [scoreRun, setScoreRun] = useState<ScanRunOption | null>(null);
  const [scoreInline, setScoreInline] = useState(true);

  // Die Poll-Schleife darf den Ladebalken nicht ausloesen, sonst flackert die
  // Tabelle alle 10 s. Deshalb der `silent`-Schalter.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (kindFilter !== 'all') params.set('job_kind', kindFilter);
    const query = params.toString() ? `?${params.toString()}` : '';

    const res = await api.get<JobListResponse>(`/watchdog/jobs${query}`);
    if (res.success) {
      setJobs(res.data.jobs);
      setCounts(res.data.counts);
      if (res.data.counts_by_kind) setCountsByKind(res.data.counts_by_kind);
      setDbProfile(res.data.db_profile);
      setError(null);
    } else {
      setError(res.error.message);
    }
    if (!silent) setLoading(false);
  }, [statusFilter, kindFilter]);

  useEffect(() => { void load(); }, [load]);

  // Leichtes Polling, solange etwas laeuft — kein WebSocket fuer die Testphase.
  const running = counts.running > 0;
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => { void loadRef.current(true); }, POLL_MS);
    return () => clearInterval(t);
  }, [running]);

  /** Jede Aktion sperrt nur sich selbst, damit parallele Klicks sich nicht ueberholen. */
  const act = async (key: string, fn: () => Promise<{ ok: boolean; message: string }>) => {
    setBusy(key);
    setError(null);
    const { ok, message } = await fn();
    setBusy(null);
    if (ok) { setToast(message); void load(true); } else { setError(message); }
  };

  const createJobs = () => act('create', async () => {
    const res = await api.post<{ created: number }>('/watchdog/jobs', {});
    return res.success
      ? { ok: true, message: `${res.data.created} Job(s) angelegt.` }
      : { ok: false, message: res.error.message };
  });

  /**
   * „Alle Jobs ausfuehren" — ueber den Start-Weg. Der Tuersteher laesst nur
   * WATCHDOG_MAX_RUNNING gleichzeitig durch, der Rest bleibt `pending`.
   */
  const runAll = () => act('run-all', async () => {
    const res = await api.post<{ dispatched?: number; dispatch_errors?: { message: string }[] }>(
      '/watchdog/jobs/run', { mode: 'start' },
    );
    if (!res.success) return { ok: false, message: res.error.message };
    const started = res.data.dispatched ?? 0;
    const held = res.data.dispatch_errors?.length ?? 0;
    return {
      ok: true,
      message: held > 0
        ? `${started} Scan(s) angestoßen, ${held} warten auf einen freien Platz.`
        : `${started} Scan(s) angestoßen — die Ergebnisse kommen per Callback.`,
    };
  });

  /**
   * „Job ausfuehren" pro Zeile. Die Arten laufen ueber verschiedene Endpunkte:
   * der Scan wird angestossen (Ergebnis kommt per Callback), die Bewertung
   * rechnet sofort.
   */
  const runOne = (job: JobListItem) => act(`run-${job.id}`, async () => {
    if (job.job_kind === 'scoring') {
      const res = await api.post('/watchdog/scoring/process', { job_id: job.id });
      return res.success
        ? { ok: true, message: `Bewertung für ${job.hostname ?? job.domain_fk} ausgeführt.` }
        : { ok: false, message: res.error.message };
    }
    const res = await api.post(`/watchdog/jobs/${job.id}/start`, {});
    return res.success
      ? { ok: true, message: `Scan für ${job.hostname ?? job.domain_fk} angestoßen — Ergebnis in 1–2 Minuten.` }
      : { ok: false, message: res.error.message };
  });

  const resetJob = (job: JobListItem) => act(`reset-${job.id}`, async () => {
    const res = await api.patch(`/watchdog/jobs/${job.id}`, { job_status: 'pending' });
    return res.success
      ? { ok: true, message: 'Job zurückgesetzt — er ist wieder startbar.' }
      : { ok: false, message: res.error.message };
  });

  const changeRunType = (job: JobListItem, runType: JobRunType) => act(`type-${job.id}`, async () => {
    const res = await api.patch(`/watchdog/jobs/${job.id}`, { run_type: runType });
    return res.success
      ? { ok: true, message: `Lauf-Art auf ${RUN_TYPE_LABEL[runType]} gesetzt.` }
      : { ok: false, message: res.error.message };
  });

  const deleteJob = (job: JobListItem) => act(`del-${job.id}`, async () => {
    const res = await api.delete(`/watchdog/jobs/${job.id}`);
    setConfirmDelete(null);
    return res.success
      ? { ok: true, message: 'Job gelöscht.' }
      : { ok: false, message: res.error.message };
  });

  // Serverseitig verlangt dieser Aufruf confirm: true. Der Weg existiert
  // ausschliesslich fuer twd_jobs — fuer keine andere Tabelle.
  const clearTable = () => act('clear', async () => {
    const res = await api.delete<{ deleted: number }>('/watchdog/jobs', { confirm: true });
    setConfirmClear(false);
    return res.success
      ? { ok: true, message: `Jobtabelle geleert (${res.data.deleted} Zeile(n)).` }
      : { ok: false, message: res.error.message };
  });

  /* ── Trigger 1: Raw-Scan starten ─────────────────────────── */

  const loadDomains = useCallback(async () => {
    if (domains.length > 0) return;
    const res = await api.get<DomainListItem[]>('/watchdog/domains');
    if (res.success) setDomains(res.data);
    else setError(res.error.message);
  }, [domains.length]);

  const openRawDialog = async (preset?: string) => {
    setRawDomain(null);
    setRawDialog(true);
    await loadDomains();
    if (preset) {
      setDomains((list) => {
        const hit = list.find((d) => d.id === preset);
        if (hit) setRawDomain(hit);
        return list;
      });
    }
  };

  const startRawScan = (domain: DomainListItem | null) => act('raw-scan', async () => {
    if (!domain) return { ok: false, message: 'Keine Domain gewählt.' };
    const res = await api.post<{ hostname: string }>('/watchdog/jobs/raw-scan', { domain_id: domain.id });
    setRawDialog(false);
    return res.success
      ? { ok: true, message: `Scan für ${domain.hostname} angestoßen — Ergebnis in 1–2 Minuten.` }
      : { ok: false, message: res.error.message };
  });

  /** Direktstart aus der Domain-Kopfzeile, ohne den Umweg über den Dialog. */
  const startRawScanForGroup = (group: DomainGroup) => act(`scan-${group.key}`, async () => {
    const res = await api.post('/watchdog/jobs/raw-scan', { domain_id: group.key });
    return res.success
      ? { ok: true, message: `Scan für ${group.hostname} angestoßen — Ergebnis in 1–2 Minuten.` }
      : { ok: false, message: res.error.message };
  });

  /* ── Trigger 2: Check and Score starten ──────────────────── */

  const loadRuns = useCallback(async (unscoredOnly: boolean) => {
    setRunsLoading(true);
    setRunsError(null);
    const path = unscoredOnly ? '/watchdog/runs/unscored' : '/watchdog/runs';
    const res = await api.get<{ runs: ScanRunOption[] }>(`${path}?limit=100`);
    if (res.success) setRuns(res.data.runs);
    else { setRuns([]); setRunsError(res.error.message); }
    setRunsLoading(false);
  }, []);

  const openScoreDialog = async () => {
    setScoreRun(null);
    setScoreDialog(true);
    await loadRuns(onlyUnscored);
  };

  const toggleUnscored = async (value: boolean) => {
    setOnlyUnscored(value);
    setScoreRun(null);
    await loadRuns(value);
  };

  const startScoring = () => act('scoring', async () => {
    if (!scoreRun) return { ok: false, message: 'Kein Lauf gewählt.' };
    const res = await api.post<{ created: boolean; dispatched: boolean }>('/watchdog/jobs/scoring', {
      run_ref: scoreRun.run_id,
      domain_ref: scoreRun.domain_ref,
      hostname: scoreRun.hostname,
      mode: scoreInline ? 'inline' : undefined,
    });
    setScoreDialog(false);
    if (!res.success) return { ok: false, message: res.error.message };
    if (!res.data.created) return { ok: true, message: 'Zu diesem Lauf gab es bereits einen Bewertungs-Job.' };
    return {
      ok: true,
      message: scoreInline
        ? `Bewertung für ${scoreRun.hostname ?? scoreRun.domain_ref} ausgeführt.`
        : `Bewertung für ${scoreRun.hostname ?? scoreRun.domain_ref} eingeplant.`,
    };
  });

  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
    setToast('In die Zwischenablage kopiert.');
  };

  /* ── Gruppierung + Suche ─────────────────────────────────── */

  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? jobs.filter((j) =>
        (j.hostname ?? '').toLowerCase().includes(needle)
        || (j.workspace_ref ?? '').toLowerCase().includes(needle))
      : jobs;
    return groupByDomain(filtered);
  }, [jobs, search]);

  // Beim Suchen aufklappen: Wer tippt, will die Treffer sehen, nicht erst
  // jede Domain einzeln aufklappen muessen.
  const searching = search.trim().length > 0;
  const isOpen = (key: string) => searching || expanded.has(key);

  const toggleGroup = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const allOpen = groups.length > 0 && groups.every((g) => isOpen(g.key));
  const toggleAll = () => setExpanded(allOpen ? new Set() : new Set(groups.map((g) => g.key)));

  const kindCounts = (kind: JobKind) => countsByKind[kind] ?? emptyCounts();
  const kindTotal = (kind: JobKind) => STATUS_KEYS.reduce((sum, st) => sum + (kindCounts(kind)[st] ?? 0), 0);

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* ── Kopf ─────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Job-Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            Eine Zeile je Domain — aufklappen zeigt die einzelnen Scans und Bewertungen.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {/* E1: nur Anzeige. Gewechselt wird in der Menueleiste. */}
          {dbProfile && (
            <Tooltip title="Datenbank-Umgebung dieser Ansicht — Wechsel über die Menüleiste">
              <Chip
                icon={<StorageIcon sx={{ fontSize: 16 }} />}
                label={dbProfile}
                size="small"
                sx={{ fontWeight: 600, bgcolor: colors.lightGrey, border: `1px solid ${colors.silver}` }}
              />
            </Tooltip>
          )}
          <Tooltip title="Neu laden">
            <span>
              <IconButton onClick={() => void load()} disabled={loading || Boolean(busy)} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* ── Suche ────────────────────────────────────────── */}
      <TextField
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Domain suchen …"
        size="small"
        fullWidth
        sx={{ mb: 2, maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: colors.black60 }} />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <Button size="small" onClick={() => setSearch('')}>zurücksetzen</Button>
            </InputAdornment>
          ) : undefined,
        }}
      />

      {/* ── Art-Filter ───────────────────────────────────── */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>Art</Typography>
        <Chip
          label={`beide ${STATUS_KEYS.reduce((s, k) => s + (counts[k] ?? 0), 0)}`}
          size="small"
          onClick={() => setKindFilter('all')}
          variant={kindFilter === 'all' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 600 }}
        />
        {KINDS.map((k) => (
          <Chip
            key={k}
            label={`${KIND_META[k].label} ${kindTotal(k)}`}
            size="small"
            onClick={() => setKindFilter(k)}
            variant={kindFilter === k ? 'filled' : 'outlined'}
            sx={{
              fontWeight: 600,
              ...(kindFilter === k
                ? { bgcolor: KIND_META[k].color, color: colors.white }
                : { borderColor: KIND_META[k].color, color: KIND_META[k].color }),
            }}
          />
        ))}
      </Stack>

      {/* ── Zaehler, zugleich Statusfilter ───────────────── */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>Status</Typography>
        <Chip
          label="alle"
          onClick={() => setStatusFilter('all')}
          variant={statusFilter === 'all' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 600 }}
        />
        {STATUS_KEYS.map((s) => {
          // Der Zaehler folgt dem Art-Filter: wer „Bewertung" gewaehlt hat, will
          // nicht die Scan-Zahlen daneben stehen sehen.
          const n = kindFilter === 'all' ? (counts[s] ?? 0) : (kindCounts(kindFilter)[s] ?? 0);
          return (
            <Chip
              key={s}
              label={`${STATUS_META[s].label} ${n}`}
              onClick={() => setStatusFilter(s)}
              variant={statusFilter === s ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600,
                ...(statusFilter === s
                  ? { bgcolor: STATUS_META[s].color, color: STATUS_META[s].text }
                  : { borderColor: STATUS_META[s].color, color: colors.black85 }),
              }}
            />
          );
        })}
      </Stack>

      {/* ── Aktionen ─────────────────────────────────────── */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={createJobs}
          disabled={Boolean(busy)}
        >
          Jobs anlegen
        </Button>
        <Button
          variant="outlined"
          startIcon={<PlaylistPlayIcon />}
          onClick={runAll}
          disabled={Boolean(busy) || (countsByKind.raw_scan?.pending ?? 0) === 0}
        >
          Alle Jobs ausführen
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Patch 01, Kap. 6: die zwei Handgriffe der Testphase. */}
        <Button
          variant="outlined"
          startIcon={<TravelExploreIcon />}
          onClick={() => void openRawDialog()}
          disabled={Boolean(busy)}
        >
          Raw-Scan starten
        </Button>
        <Button
          variant="outlined"
          startIcon={<RuleIcon />}
          onClick={openScoreDialog}
          disabled={Boolean(busy)}
          sx={{ borderColor: KIND_META.scoring.color, color: KIND_META.scoring.color }}
        >
          Check and Score starten
        </Button>

        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteSweepIcon />}
          onClick={() => setConfirmClear(true)}
          disabled={Boolean(busy) || jobs.length === 0}
        >
          Jobtabelle leeren
        </Button>
      </Stack>

      {busy && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {running && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {counts.running} Job(s) laufen gerade — die Tabelle aktualisiert sich alle 10 Sekunden.
        </Alert>
      )}

      {/* ── Gruppierte Tabelle ───────────────────────────── */}
      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : groups.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searching
                ? `Keine Domain passt zu „${search}".`
                : statusFilter === 'all' && kindFilter === 'all'
                  ? 'Keine Jobs in der Tabelle. „Jobs anlegen" erzeugt für jedes aktive Scanprofil einen Eintrag.'
                  : 'Keine Jobs, die zu diesem Filter passen.'}
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ width: 44 }}>
                  <Tooltip title={allOpen ? 'Alle zuklappen' : 'Alle aufklappen'}>
                    <span>
                      <IconButton size="small" onClick={toggleAll} disabled={searching}>
                        {allOpen ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Domain</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Läufe</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Letzter Lauf</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => {
                const open = isOpen(group.key);
                return (
                  <Fragment key={group.key}>
                    {/* ── Kopfzeile der Domain ───────────── */}
                    <TableRow
                      hover
                      onClick={() => toggleGroup(group.key)}
                      sx={{ cursor: 'pointer', bgcolor: open ? '#f8fafc' : undefined }}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{group.hostname}</Typography>
                        {group.workspace_ref && (
                          <Typography variant="caption" color="text.secondary">{group.workspace_ref}</Typography>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {KINDS.filter((k) => group.kinds[k] > 0).map((k) => (
                            <Chip
                              key={k}
                              label={`${group.kinds[k]} ${KIND_META[k].label}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.68rem', height: 20, borderColor: KIND_META[k].color, color: KIND_META[k].color }}
                            />
                          ))}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {STATUS_KEYS.filter((s) => group.counts[s] > 0).map((s) => (
                            <Chip
                              key={s}
                              label={`${group.counts[s]} ${STATUS_META[s].label}`}
                              size="small"
                              sx={{
                                bgcolor: STATUS_META[s].color, color: STATUS_META[s].text,
                                fontWeight: 600, fontSize: '0.68rem', height: 20,
                              }}
                            />
                          ))}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption">
                          {formatTime(group.latest.finished_at ?? group.latest.started_at ?? group.latest.created_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {KIND_META[group.latest.job_kind]?.label ?? group.latest.job_kind}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Neuen Raw-Scan für diese Domain anstoßen">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => startRawScanForGroup(group)}
                              disabled={Boolean(busy)}
                            >
                              <TravelExploreIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* ── Der gewaehlte Lauf im Detail ───── */}
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                        <Collapse in={open} unmountOnExit>
                          {(() => {
                            // Ohne Auswahl zeigt die Karte den neuesten Lauf.
                            const chosenId = chosenRun[group.key];
                            const job = group.jobs.find((j) => j.id === chosenId) ?? group.jobs[0];
                            if (!job) return null;
                            const meta = STATUS_META[job.job_status];
                            const kind = KIND_META[job.job_kind] ?? KIND_META.raw_scan;
                            const dur = duration(job);
                            const rowBusy = busy?.endsWith(job.id) ?? false;

                            return (
                              <Box sx={{ py: 1.5, pl: 5, pr: 2, opacity: rowBusy ? 0.5 : 1 }}>
                                {/* Lauf-Auswahl */}
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                                  <Select
                                    value={job.id}
                                    size="small"
                                    onChange={(e) => setChosenRun((prev) => ({ ...prev, [group.key]: e.target.value }))}
                                    sx={{ minWidth: 290, bgcolor: colors.white }}
                                  >
                                    {group.jobs.map((j) => (
                                      <MenuItem key={j.id} value={j.id}>{runLabel(j)}</MenuItem>
                                    ))}
                                  </Select>
                                  <Typography variant="caption" color="text.secondary">
                                    Lauf {group.jobs.indexOf(job) + 1} von {group.jobs.length}
                                  </Typography>

                                  <Box sx={{ flex: 1 }} />

                                  <Stack direction="row" spacing={0.5}>
                                    <Tooltip title={job.job_kind === 'scoring' ? 'Diese Bewertung ausführen' : 'Diesen Scan anstoßen'}>
                                      <span>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<PlayArrowIcon />}
                                          onClick={() => runOne(job)}
                                          disabled={Boolean(busy) || job.job_status === 'running'}
                                        >
                                          {job.job_kind === 'scoring' ? 'Bewerten' : 'Anstoßen'}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Zurücksetzen — macht den Job erneut startbar">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => resetJob(job)}
                                          disabled={Boolean(busy) || job.job_status === 'pending'}
                                        >
                                          <RestartAltIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Job löschen">
                                      <span>
                                        <IconButton size="small" onClick={() => setConfirmDelete(job)} disabled={Boolean(busy)}>
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                </Stack>

                                {/* Die Angaben zum Lauf */}
                                <Paper
                                  elevation={0}
                                  sx={{ border: `1px solid ${colors.silver}`, borderRadius: 1, p: 2, bgcolor: colors.white }}
                                >
                                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                                    <Detail label="Art">
                                      <Chip
                                        label={kind.label}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, borderColor: kind.color, color: kind.color }}
                                      />
                                    </Detail>

                                    <Detail label="Status">
                                      <Chip
                                        label={meta.label}
                                        size="small"
                                        sx={{ bgcolor: meta.color, color: meta.text, fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                                      />
                                    </Detail>

                                    <Detail label="Lauf-Art">
                                      <Select
                                        value={job.run_type}
                                        size="small"
                                        variant="standard"
                                        disableUnderline
                                        disabled={Boolean(busy) || job.job_status === 'running'}
                                        onChange={(e) => changeRunType(job, e.target.value as JobRunType)}
                                        sx={{ fontSize: '0.82rem' }}
                                      >
                                        <MenuItem value="night_scan">{RUN_TYPE_LABEL.night_scan}</MenuItem>
                                        <MenuItem value="manual">{RUN_TYPE_LABEL.manual}</MenuItem>
                                      </Select>
                                    </Detail>

                                    <Detail label="Versuche">
                                      <Typography variant="body2">{job.attempts}</Typography>
                                    </Detail>

                                    <Detail label="Start">
                                      <Typography variant="body2">{formatTime(job.started_at)}</Typography>
                                    </Detail>

                                    <Detail label="Ende">
                                      <Typography variant="body2">
                                        {formatTime(job.finished_at)}
                                        {dur && (
                                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                            ({dur})
                                          </Typography>
                                        )}
                                      </Typography>
                                    </Detail>

                                    <Detail label="Angelegt">
                                      <Typography variant="body2">{formatTime(job.created_at)}</Typography>
                                    </Detail>

                                    <Detail label={kind.runRefLabel}>
                                      {job.run_ref ? (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <Tooltip title={job.run_ref}>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                              {job.run_ref.slice(0, 8)}…
                                            </Typography>
                                          </Tooltip>
                                          <Tooltip title="run_ref kopieren">
                                            <IconButton size="small" onClick={() => copy(job.run_ref as string)}>
                                              <ContentCopyIcon sx={{ fontSize: 13 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </Detail>
                                  </Stack>

                                  {/* Fehlergrund: bei einem gescheiterten Lauf will man ihn sofort sehen. */}
                                  {job.error_detail && (
                                    <Alert
                                      severity={job.job_status === 'failed' ? 'error' : 'warning'}
                                      variant="outlined"
                                      icon={<ErrorOutlineIcon fontSize="small" />}
                                      sx={{ mt: 2 }}
                                    >
                                      <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                        {job.error_detail}
                                      </Typography>
                                    </Alert>
                                  )}
                                </Paper>
                              </Box>
                            );
                          })()}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ── Trigger 1: Raw-Scan starten ──────────────────── */}
      <Dialog open={rawDialog} onClose={() => setRawDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Raw-Scan starten</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Legt für eine Domain einen Scan-Job an und stößt ihn an — unabhängig davon, ob
            für sie schon ein Job offen ist.
          </DialogContentText>
          <Autocomplete
            options={domains}
            value={rawDomain}
            onChange={(_e, v) => setRawDomain(v)}
            getOptionLabel={(d) => d.hostname}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Domain" autoFocus />}
            renderOption={(props, d) => (
              <li {...props} key={d.id}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.hostname}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.event_count} Events · {d.gtm_container_id ?? 'kein Container'}
                  </Typography>
                </Box>
              </li>
            )}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Der Scan wird angestoßen und läuft 1–2 Minuten. Das Ergebnis kommt über den
            Callback — die Tabelle aktualisiert sich dann von selbst.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawDialog(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => startRawScan(rawDomain)} disabled={!rawDomain || Boolean(busy)}>
            Scan starten
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Trigger 2: Check and Score starten ───────────── */}
      <Dialog open={scoreDialog} onClose={() => setScoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Check and Score starten</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Meldet einen bestehenden Raw-Lauf zur Bewertung an. Der Job trägt den Lauf als
            <code> run_ref</code> — das ist hier der Gegenstand der Bewertung, nicht ihr Ergebnis.
          </DialogContentText>

          <FormControlLabel
            control={<Switch checked={onlyUnscored} onChange={(e) => void toggleUnscored(e.target.checked)} />}
            label="Nur noch nicht bewertete Läufe zeigen"
          />

          {runsError && <Alert severity="warning" sx={{ my: 1 }}>{runsError}</Alert>}

          <Autocomplete
            sx={{ mt: 1 }}
            options={runs}
            value={scoreRun}
            loading={runsLoading}
            onChange={(_e, v) => setScoreRun(v)}
            getOptionLabel={(r) => `${r.hostname ?? r.domain_ref} · ${formatTime(r.run_at)}`}
            isOptionEqualToValue={(a, b) => a.run_id === b.run_id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Raw-Lauf"
                helperText={runsLoading ? 'Läufe werden geladen…' : `${runs.length} Lauf/Läufe im Fenster`}
              />
            )}
            renderOption={(props, r) => (
              <li {...props} key={r.run_id}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.hostname ?? r.domain_ref}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {formatTime(r.run_at)} · {r.run_status ?? '?'} · {r.run_id.slice(0, 8)}…
                  </Typography>
                </Box>
              </li>
            )}
          />

          <FormControlLabel
            sx={{ mt: 2 }}
            control={<Switch checked={scoreInline} onChange={(e) => setScoreInline(e.target.checked)} />}
            label="Sofort ausführen statt in die Score-Queue geben"
          />
          <Alert severity="info" sx={{ mt: 1 }}>
            Der Scoring-Endpunkt ist noch das Gerüst: Er prüft Lauf und Profil-Baseline und schließt
            den Job ab. Die 13 Regeln kommen mit dem Check-and-Score-Guide.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialog(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={startScoring}
            disabled={!scoreRun || Boolean(busy)}
            sx={{ bgcolor: KIND_META.scoring.color }}
          >
            Bewertung starten
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bestaetigung: einzelne Zeile ─────────────────── */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Job löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Der Job für <strong>{confirmDelete?.hostname ?? confirmDelete?.domain_fk}</strong> wird aus der
            Tabelle entfernt. Eine bereits erzeugte Raw-Zeile bleibt bestehen.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Abbrechen</Button>
          <Button color="error" onClick={() => confirmDelete && deleteJob(confirmDelete)}>Löschen</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bestaetigung: ganze Tabelle ──────────────────── */}
      <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
        <DialogTitle>Jobtabelle leeren?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Alle Einträge aus <code>twd_jobs</code> werden gelöscht — Scans wie Bewertungen.
            Laufende Jobs bleiben unangetastet. Die erzeugten Raw-Zeilen in BigQuery sind davon
            nicht betroffen.
          </DialogContentText>
          <Alert severity="warning">
            Dieser Weg existiert ausschließlich für die Jobtabelle — für keine andere Tabelle der Anwendung.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={clearTable}>Tabelle leeren</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
